// frontend/src/Home.js
import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaFileInvoiceDollar, 
  FaBell, 
  FaChartLine, 
  FaShieldAlt, 
  FaUsers, 
  FaBuilding,
  FaQuoteRight,
  FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Home.css';

function Home() {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, []);

  // Datos de características
  const features = [
    {
      icon: FaFileInvoiceDollar,
      title: "Gestión Financiera Inteligente",
      description: "Control automatizado de alícuotas, pagos en línea y reportes detallados para una transparencia total y eficiente."
    },
    {
      icon: FaBell,
      title: "Comunicación Instantánea",
      description: "Notificaciones en tiempo real, tableros de anuncios digitales y chats integrados para una comunidad siempre conectada."
    },
    {
      icon: FaChartLine,
      title: "Análisis y Reportes Avanzados",
      description: "Dashboards intuitivos con métricas clave, tendencias financieras y operativas para decisiones informadas."
    },
    {
      icon: FaShieldAlt,
      title: "Seguridad Reforzada",
      description: "Gestión de accesos, registro de visitantes y cámaras de seguridad integradas para proteger su comunidad 24/7."
    },
    {
      icon: FaUsers,
      title: "Comunidad Conectada",
      description: "Foros, encuestas y reserva de áreas comunes para fomentar la interacción y participación de los residentes."
    },
    {
      icon: FaBuilding,
      title: "Mantenimiento Simplificado",
      description: "Reporte de incidencias, seguimiento de tareas y gestión de proveedores para un condominio impecable."
    }
  ];

  // Datos de beneficios
  const benefits = [
    {
      icon: FaShieldAlt,
      title: "Transparencia Total",
      description: "Acceso claro y auditable a toda la información financiera y administrativa del condominio en cualquier momento."
    },
    {
      icon: FaUsers,
      title: "Comunicación Efectiva",
      description: "Un canal directo y centralizado entre la administración y los residentes para resolver problemas y compartir información vital."
    },
    {
      icon: FaBuilding,
      title: "Gestión Simplificada",
      description: "Optimización de todos los procesos administrativos y operativos, ahorrando tiempo y recursos valiosos."
    }
  ];

  // Datos de testimoniales
  const testimonials = [
    {
      quote: "UrbanQ ha transformado la administración de nuestro edificio. La eficiencia y la transparencia han mejorado exponencialmente.",
      author: "Ing. Sofía Vargas",
      role: "Administradora Senior, Edificio Panorama",
      initials: "SV"
    },
    {
      quote: "Finalmente una plataforma que entiende las necesidades de nuestra comunidad. La facilidad de uso es impresionante.",
      author: "Lic. Andrés Zambrano",
      role: "Presidente Junta de Propietarios, Condominio La Vista",
      initials: "AZ"
    },
    {
      quote: "Ahora, pagar mis alícuotas y estar al tanto de las novedades es increíblemente sencillo. ¡Felicito al equipo de UrbanQ!",
      author: "Dra. Elena Castro",
      role: "Residente, Torres del Río",
      initials: "EC"
    }
  ];

  return (
    <div className="home-page">
      {/* Animated Background */}
      <div className="animated-background"></div>

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="hero-content" data-aos="fade-right">
              <div className="badge-new">
                <span className="badge-icon">🚀</span>
                <span>La Plataforma del Futuro</span>
              </div>
              
              <h1 className="hero-title">UrbanQ</h1>
              <p className="hero-subtitle">"Smart living, Ecuadorian soul"</p>
              
              <p className="hero-description">
                La plataforma definitiva para la gestión de condominios y comunidades 
                residenciales en Ecuador. Con herramientas intuitivas y una interfaz de 
                usuario avanzada, transformamos la interacción entre administradores y residentes.
              </p>

              <div className="hero-buttons">
                <Link to="/dashboard" className="btn-hero-primary">
                  <span>Comienza tu Transformación</span>
                  <FaArrowRight className="btn-icon" />
                </Link>
                <Link to="/planes" className="btn-hero-secondary">
                  Ver Planes
                </Link>
              </div>
            </Col>

            <Col lg={6} className="hero-image-col" data-aos="fade-left" data-aos-delay="200">
              <div className="hero-image-wrapper">
                <img src="/images/edificio.png" alt="UrbanQ Platform" className="hero-image" />
                <div className="floating-element floating-1"></div>
                <div className="floating-element floating-2"></div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Container>
          <div className="section-header" data-aos="fade-up">
            <h2 className="section-title">Soluciones que Impulsan tu Comunidad</h2>
            <div className="title-underline"></div>
          </div>

          <Row className="g-4">
            {features.map((feature, index) => (
              <Col lg={4} md={6} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="feature-card">
                  <div className="feature-icon-wrapper">
                    <feature.icon className="feature-icon" />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                  <div className="feature-glow"></div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* About Section */}
      <section className="about-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="about-image-col" data-aos="fade-right">
              <div className="about-image-wrapper">
                <img src="/images/community.jpg" alt="Comunidad UrbanQ" className="about-image" />
              </div>
            </Col>

            <Col lg={6} className="about-content" data-aos="fade-left">
              <h2 className="about-title">Construyendo el Futuro de la Comunidad</h2>
              <p className="about-text">
                UrbanQ no es solo una plataforma, es la visión de un futuro donde la 
                administración de comunidades es fluida, transparente y eficiente. 
                Liberamos a los administradores de tareas repetitivas y empoderamos a 
                los residentes con control y acceso sin precedentes.
              </p>

              <div className="benefits-list">
                {benefits.map((benefit, index) => (
                  <div 
                    className="benefit-item" 
                    key={index}
                    data-aos="fade-up" 
                    data-aos-delay={index * 100}
                  >
                    <div className="benefit-icon-wrapper">
                      <benefit.icon className="benefit-icon" />
                    </div>
                    <div className="benefit-text">
                      <h4 className="benefit-title">{benefit.title}</h4>
                      <p className="benefit-description">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <Container>
          <div className="section-header" data-aos="fade-up">
            <h2 className="section-title">Lo que Dicen Nuestros Clientes</h2>
            <div className="title-underline"></div>
          </div>

          <Row className="g-4">
            {testimonials.map((testimonial, index) => (
              <Col lg={4} md={6} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="testimonial-card">
                  <FaQuoteRight className="quote-icon" />
                  <p className="testimonial-text">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">{testimonial.initials}</div>
                    <div className="author-info">
                      <h5 className="author-name">{testimonial.author}</h5>
                      <p className="author-role">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <div className="cta-content" data-aos="zoom-in">
            <h2 className="cta-title">¿Listo para el Futuro de la Gestión?</h2>
            <p className="cta-description">
              Descubre cómo UrbanQ puede elevar la experiencia de vivir y administrar 
              en tu comunidad.
            </p>
            <div className="cta-buttons">
              <Link to="/contactos" className="btn-cta-primary">
                Contáctanos para una Demo
              </Link>
              <Link to="/planes" className="btn-cta-secondary">
                Ver Precios
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default Home;